import type { ResultOf } from '@graphql-typed-document-node/core'
import { ReadMoreAccordion } from 'macaron-frontend-ui'
import { useCallback, useRef } from 'react'
import { Pagination } from 'swiper/modules'
import { Swiper, type SwiperClass, SwiperSlide } from 'swiper/react'
import Image from '~/components/image/Image'
import { useIsPC } from '~/lib/ua'
import type { RecipeVariantProps } from '~/styled-system/css'
import { css, cx, sva } from '~/styled-system/css'
import { OverlayScrollButton } from '~/ui/IconButton'
import { CourseNoImage } from './CourseNoImage'
import type { CourseSelectionInfo } from './CourseSelectionInfo.frag'

type Props = {
  courseName?: string
  images: ResultOf<typeof CourseSelectionInfo>['images']
  imageWidth: number
  imageHeight: number
  menuText: string
  showReadMoreAccordion?: boolean
}

const styles = sva({
  slots: ['menu'],
  base: {
    menu: {
      mt: '3',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-all',
      _pc: {
        mt: '2',
        p: '4',
        borderRadius: 'md',
        bgColor: 'gray.100',
        fontSize: 'md',
      },
    },
  },
  variants: {
    bg: {
      white: {
        menu: {
          _pc: {
            bgColor: 'white',
            p: '0',
          },
        },
      },
    },
  },
})

export function CourseMenu({
  courseName,
  images,
  imageWidth,
  imageHeight,
  menuText,
  bg,
  showReadMoreAccordion = true,
}: Props & RecipeVariantProps<typeof styles>) {
  const isPc = useIsPC()
  const classes = styles({ bg })
  return (
    <>
      {isPc && courseName && (
        <h2 className={css({ fontSize: '2xl', fontWeight: 'bold' })}>{courseName}</h2>
      )}
      <CourseMenuImages images={images} imageWidth={imageWidth} imageHeight={imageHeight} />
      <div className="mt-2 space-y-2">
        {!isPc && <h2 className="text-lg font-bold">{courseName ?? 'メニュー'}</h2>}
        {isPc && !courseName && <h2 className="mt-5 text-xl font-bold">メニュー</h2>}
        {showReadMoreAccordion ? (
          <ReadMoreAccordion color="blue" size="small">
            <p className="whitespace-pre-wrap">{menuText}</p>
            <p className="text-sm text-neutral-600">
              ※メニューは仕入状況等により変更となる場合がございます。予めご了承ください。
            </p>
          </ReadMoreAccordion>
        ) : (
          <>
            <div className={classes.menu}>
              <p>{menuText}</p>
              <p className={css({ mt: '2', fontSize: 'sm', color: 'gray.700', _pc: { mt: '4' } })}>
                ※メニューは仕入状況等により変更となる場合がございます。予めご了承ください。
              </p>
            </div>
          </>
        )}
      </div>
    </>
  )
}

function CourseMenuImages({
  images,
  imageWidth,
  imageHeight,
}: Pick<Props, 'images' | 'imageWidth' | 'imageHeight'>) {
  const sd = !useIsPC()

  const swiper = useRef<SwiperClass | null>(null)
  const next = useCallback(() => swiper.current?.slideNext(), [])
  const prev = useCallback(() => swiper.current?.slidePrev(), [])
  const imageStyle = css({
    h: 'auto',
    w: 'full',
    objectFit: 'contain',
    borderRadius: 'sm',
  })

  // PPGの場合、画像がない場合はコース用のNoImageを表示
  if (import.meta.env.VITE_MODE === 'ppg' && images.length === 0) {
    return <CourseNoImage width={imageWidth} height={imageHeight} className={imageStyle} />
  }

  return (
    // SPの予約入力内のプラン詳細モーダルで、画像とテキスト間にspaceY:'5'がつかないようにするため、divで囲む
    <div>
      <Swiper
        onSwiper={(s) => (swiper.current = s)}
        modules={[Pagination]}
        pagination={true}
        loop={true}
        className={css({ position: 'relative' })}
        role="group"
      >
        {images.map((image, index) => {
          return (
            <SwiperSlide key={index}>
              <Image
                alt={image.name}
                src={image.url}
                width={imageWidth}
                height={imageHeight}
                className={imageStyle}
              />
            </SwiperSlide>
          )
        })}
        {!sd && images.length >= 2 && (
          <>
            <OverlayScrollButton onClick={prev} direction="left" />
            <OverlayScrollButton onClick={next} direction="right" />
          </>
        )}
      </Swiper>
      <div className={cx('mt-1 text-right text-xs', css({ color: 'gray.600' }))}>
        ※写真はイメージです
      </div>
    </div>
  )
}

export default CourseMenu
